import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../core/styles.constants';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import { inkFor, tint } from '../../core/admin-surface.styles';

// Layout/spacing follow the approved rooms mockup; every colour comes from the app's own palette
// (StyleConstants), which is what the mockup's navy/blue/amber trio maps onto.
export const ACCENTS = [StyleConstants.DARK_BLUE_COLOR, StyleConstants.MEDIUM_BLUE_COLOR, StyleConstants.ORANGE_COLOR];

// Accents are handed out by list position, not by hashing the name: neighbouring departments then
// never land on the same colour, and a department keeps the same colour in both cards on the tab.
export const accentAt = (index: number): string => ACCENTS[((index % ACCENTS.length) + ACCENTS.length) % ACCENTS.length];

// Rooms that belong to no department stay neutral rather than borrowing a department's colour.
export const NEUTRAL_ACCENT = StyleConstants.GREY_COLOR;

export const workspaceGrid = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 340px) minmax(0, 1fr)',
  gap: 24,
  alignItems: 'start',
});

export const departmentRow: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 20px',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  cursor: 'pointer',
  $nest: {
    '&:hover': {
      background: DashboardTheme.gray50,
    },
    '&:last-child': {
      borderBottom: 0,
    },
  },
};

export const avatar = (accent: string): CSSProperties => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: '0.02em',
  background: tint(accent, 0.14),
  color: inkFor(accent),
});

export const departmentName: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  wordBreak: 'break-word',
};

export const departmentMeta: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 400,
  color: DashboardTheme.gray500,
  display: 'block',
};

export const iconButton: NestedCSSProperties = {
  border: 0,
  background: 'none',
  padding: 6,
  borderRadius: '50%',
  cursor: 'pointer',
  lineHeight: 0,
  color: DashboardTheme.gray500,
  $nest: {
    '&:hover': {
      background: DashboardTheme.gray100,
      color: StyleConstants.MEDIUM_BLUE_COLOR,
    },
  },
};

export const toolbar = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'center',
  justifyContent: 'space-between',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  padding: '20px 20px 0',
  flexWrap: 'wrap',
});

export const searchWrap = (isMobile: boolean): CSSProperties => ({
  position: 'relative',
  flex: isMobile ? undefined : '0 1 260px',
  width: isMobile ? '100%' : undefined,
});

export const searchIcon: CSSProperties = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  color: DashboardTheme.gray500,
  lineHeight: 0,
  pointerEvents: 'none',
};

export const searchInput = (isMobile: boolean): CSSProperties => ({
  width: '100%',
  padding: '9px 12px 9px 36px',
  fontSize: 13.5,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: StyleConstants.DARK_BLUE_COLOR,
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  background: StyleConstants.WHITE_COLOR,
  boxSizing: isMobile ? 'border-box' : undefined,
});

// Segmented control: one bordered box with hairline dividers, as in the mockup — not loose chips.
export const filterGroup: CSSProperties = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  overflow: 'hidden',
};

export const filterButton = (active: boolean, isFirst: boolean): CSSProperties => ({
  border: 0,
  borderLeft: isFirst ? 0 : `1px solid ${DashboardTheme.gray300}`,
  background: active ? tint(StyleConstants.DARK_BLUE_COLOR, 0.1) : StyleConstants.WHITE_COLOR,
  color: active ? StyleConstants.DARK_BLUE_COLOR : DashboardTheme.gray700,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  fontWeight: 600,
  padding: '7px 14px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const cardBody: CSSProperties = {
  padding: 20,
};

export const section: CSSProperties = {
  marginBottom: 28,
};

export const sectionHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 12,
};

export const sectionPill = (accent: string): CSSProperties => ({
  background: tint(accent, 0.14),
  color: inkFor(accent),
  borderRadius: 6,
  padding: '3px 9px',
  fontSize: 11.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

export const sectionTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const sectionRule: CSSProperties = {
  flex: 1,
  height: 1,
  background: DashboardTheme.gray200,
};

export const sectionMeta: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  whiteSpace: 'nowrap',
};

export const roomGrid = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gap: 16,
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(230px, 1fr))',
});

export const roomCard: NestedCSSProperties = {
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  padding: 20,
  cursor: 'pointer',
  transitionDuration: '150ms',
  transitionProperty: 'border-color, box-shadow, transform',
  $nest: {
    '&:hover': {
      borderColor: StyleConstants.MEDIUM_BLUE_COLOR,
      boxShadow: '0 8px 24px rgba(16, 24, 40, .08)',
      transform: 'translateY(-2px)',
    },
  },
};

export const roomTop: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
};

export const roomTile = (accent: string): CSSProperties => ({
  width: 40,
  height: 40,
  borderRadius: 10,
  display: 'grid',
  placeItems: 'center',
  flex: '0 0 auto',
  background: tint(accent, 0.12),
  color: inkFor(accent),
});

export const roomHeading: CSSProperties = {
  minWidth: 0,
  flex: 1,
};

export const roomName: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const roomKind: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
};

export const lockIcon: CSSProperties = {
  color: DashboardTheme.gray300,
  marginTop: 2,
  flex: '0 0 auto',
};

export const seatsRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  marginTop: 16,
};

export const seatsValue: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  lineHeight: 1,
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const seatsLabel: CSSProperties = {
  fontSize: 13,
  color: DashboardTheme.gray500,
};

export const capacityBar: CSSProperties = {
  height: 6,
  borderRadius: 3,
  background: DashboardTheme.gray100,
  marginTop: 8,
  overflow: 'hidden',
};

export const capacityBarFill = (accent: string, percentage: number): CSSProperties => ({
  display: 'block',
  height: '100%',
  borderRadius: 3,
  width: `${percentage}%`,
  background: accent,
});

// Info banner, matching the mockup's light-blue callout.
export const ALERT_BACKGROUND = '#EAF3FC';
export const ALERT_INK = '#1B4E7F';

export const alert: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  marginTop: 24,
  background: ALERT_BACKGROUND,
  color: ALERT_INK,
  borderRadius: 10,
  padding: '12px 16px',
  fontSize: 13,
  lineHeight: 1.5,
};

export const alertLink: CSSProperties = {
  color: ALERT_INK,
  fontWeight: 600,
};

export const alertIcon: CSSProperties = {
  flex: '0 0 auto',
  color: StyleConstants.MEDIUM_BLUE_COLOR,
};
